import { PATTERN_VARIANTS } from './editor.config'
import { IProductConfig } from './editor.types'

const basePatternProductConfig: IProductConfig = {
  shape: 'rectangle',
  enabledWidgets: {
    logo: false,
    pattern: true,
  },
  bleedWidthOverride: 10,
  bleedHeightOverride: 10,
  pdfLayoutConfig: {
    artworkPlacing: 'full',
    separateArtboardForKraftType: true,
  },
}

const configPresetForOneLinePatternProducts: IProductConfig = {
  ...basePatternProductConfig,
  pdfLayoutConfig: {
    ...basePatternProductConfig?.pdfLayoutConfig,
    artworkPlacing: 'centered',
  },
  patternWidgetConfig: {
    variant: PATTERN_VARIANTS.LINE,
  },
}

const configPresetForFixedLogoProducts: IProductConfig = {
  shape: 'circle',
  enabledWidgets: {
    logo: true,
    pattern: false,
  },
  bleedWidthOverride: 5,
  bleedHeightOverride: 5,
  pdfLayoutConfig: {
    artworkPlacing: 'full',
    separateArtboardForKraftType: true,
  },
  logoWidgetConfig: {
    positionIsConfigurable: false,
  },
}

const configPresetForCofigurableLogoProducts: IProductConfig = {
  ...configPresetForFixedLogoProducts,
  pdfLayoutConfig: {
    ...configPresetForFixedLogoProducts?.pdfLayoutConfig,
    artworkPlacing: 'centered',
  },
  logoWidgetConfig: {
    ...configPresetForFixedLogoProducts?.logoWidgetConfig,
    positionIsConfigurable: true,
  },
}

const editorProductsConfig: Record<string, IProductConfig> = {
  /** Pattern Products */
  'custom-coated-paper': basePatternProductConfig,
  'custom-printed-tissue-paper': basePatternProductConfig,
  'custom-multi-colored-tissue-paper': basePatternProductConfig,
  'custom-kraft-tissue-paper': basePatternProductConfig,
  'custom-foodsafe-paper': basePatternProductConfig,
  'custom-sandwich-wrap-paper': basePatternProductConfig,
  'custom-deli-paper': basePatternProductConfig,
  'custom-food-basket-liners': basePatternProductConfig,
  'custom-packing-tape': configPresetForOneLinePatternProducts,
  'custom-reinforced-gummed-packing-tape':
    configPresetForOneLinePatternProducts,
  'noissue-custom-washi-tape': configPresetForOneLinePatternProducts,
  'custom-masking-tape': configPresetForOneLinePatternProducts,

  /** Logo Products */
  'custom-printed-cards': configPresetForFixedLogoProducts,
  'custom-menu-cards': configPresetForFixedLogoProducts,
  'custom-printed-stickers': configPresetForFixedLogoProducts,
  'custom-die-cut-stickers': configPresetForFixedLogoProducts,
  'custom-multi-design-die-cut-sticker-sheets':
    configPresetForFixedLogoProducts,
  'custom-die-cut-sticker-roll': configPresetForFixedLogoProducts,
  'custom-coated-labels': configPresetForFixedLogoProducts,
  'custom-sticker-rolls': configPresetForFixedLogoProducts,
  'custom-sticker-rolls-coated': configPresetForFixedLogoProducts,
  'custom-eco-friendly-stickers': configPresetForFixedLogoProducts,
  'custom-roll-labels': configPresetForFixedLogoProducts,
  'custom-candle-labels': configPresetForFixedLogoProducts,
  'custom-recycled-satin-ribbon': {
    ...configPresetForFixedLogoProducts,
    bleedWidthOverride: 0,
    bleedHeightOverride: 0,
  },

  /** Two sided products */
  'custom-tote-bags': {
    ...configPresetForCofigurableLogoProducts,
    decorations: ['handles'],
    isTwoSided: true,
    sidesShareColors: true,
  },
  'custom-poly-mailers': {
    ...configPresetForCofigurableLogoProducts,
    decorations: ['flap'],
  },
  'wholesale-custom-poly-mailers': {
    ...configPresetForCofigurableLogoProducts,
    decorations: ['flap'],
  },
  'custom-recycled-die-cut-handle-bags': configPresetForCofigurableLogoProducts,
  'custom-recycled-mailers-with-handles':
    configPresetForCofigurableLogoProducts,
  'custom-kraft-mailers': {
    ...configPresetForCofigurableLogoProducts,
    decorations: ['flap'],
  },
  'custom-canvas-tote-bags': {
    ...configPresetForCofigurableLogoProducts,
    decorations: ['handles'],
    isTwoSided: true,
    sidesShareColors: true,
  },
  'custom-drawstring-bags': {
    ...configPresetForCofigurableLogoProducts,
    decorations: ['side-pull'],
    isTwoSided: true,
    sidesShareColors: true,
  },
  'custom-paper-bags-with-handles': configPresetForCofigurableLogoProducts,
  'custom-food-delivery-bags': configPresetForCofigurableLogoProducts,
  'custom-sandwich-bags': configPresetForCofigurableLogoProducts,

  'custom-takeout-bags-with-handles': {
    ...configPresetForCofigurableLogoProducts,
    decorations: ['handles'],
    isTwoSided: true,
    safeZoneOffsetsMm: {
      top: 100,
      bottom: 25,
      left: 25,
      right: 25,
    },
    sidesShareColors: true,
  },

  'custom-paper-shopping-bags': {
    ...configPresetForCofigurableLogoProducts,
    decorations: ['handles'],
    safeZoneOffsetsMm: {
      top: 100,
      bottom: 25,
      left: 25,
      right: 25,
    },
    sidesShareColors: true,
  },

  'wholesale-custom-paper-bags-with-handles': {
    ...configPresetForCofigurableLogoProducts,
    decorations: ['handles'],
    isTwoSided: true,
    safeZoneOffsetsMm: {
      top: 5,
      bottom: 5,
      left: 5,
      right: 5,
    },
    safeZoneOffsetPerSizeOverride: {
      "205mm x 135mm x 95mm) (8'' x 5.25'' x 3.75'')": {
        top: 5,
        bottom: 5,
        left: 5,
        right: 5,
      },
    },
    sidesShareColors: true,
  },

  'wholesale-custom-paper-bags-food-service': {
    ...configPresetForCofigurableLogoProducts,
    decorations: ['handles'],
    isTwoSided: true,
    sidesShareColors: true,
  },
  'custom-coasters': configPresetForFixedLogoProducts,
  'custom-hang-tags': {
    ...configPresetForFixedLogoProducts,
    isTwoSided: true,
  },
  'custom-compostable-mailers': {
    ...configPresetForCofigurableLogoProducts,
    decorations: ['left-flap'],
  },
  'wholesale-custom-compostable-mailers': {
    ...configPresetForCofigurableLogoProducts,
    decorations: ['left-flap'],
  },
  'wholesale-custom-flexible-kraft-mailers': {
    ...configPresetForCofigurableLogoProducts,
  },
}

const productSizeToCutCornersSizeMapper: Record<
  string,
  Record<string, number>
> = {
  'custom-hang-tags': {
    '50mm x 50mm (2" x 2")': 12.5,
    '50mm x 152mm (2" x 6")': 15,
    '54mm x 90mm (2" x 3.5")': 8.8,
    '76mm x 76mm (3" x 3")': 19,
  },
}

// Some products doesn't have Product Color Option and are Multi Color by default
// We use this array to register them and check when we need to decide
// wheter we should enable multi color mode for certain product
const purelyMultiColorProducts = [
  'custom-printed-stickers',
  'custom-sticker-rolls',
  'custom-roll-labels',
  'custom-kraft-mailers',
  'custom-multi-colored-tissue-paper',
  'custom-eco-friendly-stickers',
  'custom-die-cut-stickers',
  'custom-die-cut-sticker-roll',
  'custom-multi-design-die-cut-sticker-sheets',
  'custom-candle-labels',
  'custom-packing-tape',
  'noissue-custom-washi-tape',
  'custom-reinforced-gummed-packing-tape',
  'custom-masking-tape',
  'custom-printed-cards',
  'custom-hang-tags',
  'custom-sticker-rolls-coated',
  'custom-sandwich-bags',
  'custom-coasters',
  'custom-menu-cards',
]

export {
  editorProductsConfig,
  productSizeToCutCornersSizeMapper,
  purelyMultiColorProducts,
}
